'use client';

import { createRoute, Link } from '@tanstack/react-router';
import { useTheme } from 'next-themes';
import { type ReactNode, Suspense, useEffect, useState } from 'react';

import MarketingLayout from '@/components/layouts/MarketingLayout';
import { WaitlistCTA } from '@/components/marketing/WaitlistCTA';
import { JsonLd } from '@/components/seo/JsonLd';
import { Skeleton } from '@/components/ui';
import { appHasMounted, markAppMounted } from '@/hooks/appMounted';
import { seoHead } from '@/lib/seo/routeMeta';
import { theme } from '@/lib/ui/theme';

import { Route as rootRoute } from './__root';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/faq',
  head: () => seoHead('/faq'),
  component: Faq,
});

// `answerText` mirrors a JSX answer as plain text for the FAQPage structured data
// (string answers are used directly). Keep it in sync with the rendered answer.
const faqs: { q: string; a: ReactNode; answerText?: string }[] = [
  {
    q: 'Which languages does Mindrealm support?',
    answerText:
      'Go, Python, TypeScript, and Rust. Every check runs across all four, held to the same bar.',
    a: (
      <>
        Go, Python, TypeScript, and Rust. Every check runs across all four, held
        to the same bar. See the{' '}
        <Link
          to="/docs"
          hash="review-categories"
          className="underline"
          style={{ textDecorationColor: theme.colors.semantic.info }}
        >
          full list of what it reviews
        </Link>
        .
      </>
    ),
  },
  {
    q: 'Which agents does Mindrealm work with?',
    a: 'Mindrealm reviews code from any agent or developer. What matters is the code, not who wrote it. For the automatic stop hook auto-fix loop, one command wires up Claude Code, Codex, Kimi Code, and Antigravity CLI.',
  },
  {
    q: 'What does Mindrealm check for?',
    answerText:
      'Correctness and the failure modes that surface later: architecture and structure, reliability, security, concurrency, and performance. Over 75 checks, each built for one specific kind of failure.',
    a: (
      <>
        Correctness and the failure modes that surface later: architecture and
        structure, reliability, security, concurrency, and performance. Over 75
        checks, each built for one specific kind of failure. See the{' '}
        <Link
          to="/docs"
          hash="review-categories"
          className="underline"
          style={{ textDecorationColor: theme.colors.semantic.info }}
        >
          full list of what it reviews
        </Link>
        .
      </>
    ),
  },
  {
    q: 'How is this different from a linter?',
    a: "Linters check style and known rule violations. Mindrealm reviews whether the code is actually correct: logic that's subtly wrong, errors that get checked and then ignored, tests that pass without proving anything, untrusted input reaching a query or a shell, and concurrency and performance traps that only bite at scale. That's over 75 checks, each built for one specific kind of failure.",
  },
  {
    q: 'How is this different from other AI code reviewers?',
    a: 'Mindrealm uses 75+ deterministic checks to catch concrete correctness failures, so the same code produces the same findings. On Claude Code, Codex, Kimi Code, and Antigravity CLI, a stop hook runs when your agent finishes, feeds the findings back, and the agent fixes what it can before Mindrealm checks again.',
  },
  {
    q: 'How do I get started, and what does it cost?',
    answerText:
      'Start with access to the GitHub App. Install it once for your organization, invite your team, then wire Mindrealm into Claude Code, Codex, Kimi Code, or Antigravity CLI through the hook. You can also review pull requests or run Mindrealm from the terminal. Pricing is a flat $30 per seat each month with a 7-day free trial.',
    a: (
      <>
        <WaitlistCTA
          label="Install the GitHub App"
          source="faq_get_started"
          variant="inline"
        />{' '}
        to start. The App is installed once for your organization. Invite your
        team, then wire Mindrealm into Claude Code, Codex, Kimi Code, or
        Antigravity CLI through the hook. You can also review pull requests or
        run Mindrealm from the terminal. The{' '}
        <Link
          to="/getting-started"
          className="underline"
          style={{ textDecorationColor: theme.colors.semantic.info }}
        >
          getting started guide
        </Link>{' '}
        walks through both. Pricing is a flat $30 per seat each month with a
        7-day free trial. Full details on the{' '}
        <Link
          to="/pricing"
          className="underline"
          style={{ textDecorationColor: theme.colors.semantic.info }}
        >
          pricing page
        </Link>
        .
      </>
    ),
  },
  {
    q: 'Does Mindrealm use AI or an LLM to review my code?',
    a: 'Your review findings never come from an LLM. Every check is deterministic, so the same code produces the same findings every run. We use AI elsewhere in the product, but never to decide what counts as a finding.',
  },
  {
    q: 'Is my code stored or used to train anything?',
    a: 'Mindrealm clones your repository only to review it. It uses your code solely to produce your findings, and never trains on it or shares it.',
  },
  {
    q: 'What does the GitHub App need access to?',
    a: 'Read access to the repositories you choose, and permission to post review results on your pull requests. Nothing more.',
  },
  {
    q: 'Do I need the GitHub App if I only use the CLI?',
    answerText:
      'Yes. Start with access to the GitHub App. One installation sets up your organization, even if you mostly review from your terminal.',
    a: (
      <>
        Yes.{' '}
        <WaitlistCTA
          label="Install the GitHub App"
          source="faq_github_required"
          variant="inline"
        />{' '}
        first. One installation sets up your organization, even if you mostly
        review from your terminal.
      </>
    ),
  },
  {
    q: 'What if a finding is wrong?',
    answerText:
      'You can silence a single check where you disagree. It stays recorded and reviewable, so a suppression is never silent and never hides a real problem from your team.',
    a: (
      <>
        You can{' '}
        <Link
          to="/docs"
          hash="suppressions"
          className="underline"
          style={{ textDecorationColor: theme.colors.semantic.info }}
        >
          silence a single check
        </Link>{' '}
        where you disagree. It stays recorded and reviewable, so a suppression
        is never silent and never hides a real problem from your team.
      </>
    ),
  },
  {
    q: 'Can I self-host Mindrealm?',
    answerText:
      "Mindrealm is a hosted service for individuals and teams, so there is nothing to run or maintain yourself. We don't offer self-hosting today, but if your organization needs an on-premises or enterprise deployment, get in touch.",
    a: (
      <>
        Mindrealm is a hosted service for individuals and teams, so there is
        nothing to run or maintain yourself. We don't offer self-hosting today,
        but if your organization needs an on-premises or enterprise deployment,{' '}
        <Link
          to="/contact"
          className="underline"
          style={{ textDecorationColor: theme.colors.semantic.info }}
        >
          get in touch
        </Link>{' '}
        and we'll talk.
      </>
    ),
  },
  {
    q: 'Do you offer consulting or hands-on help?',
    answerText:
      'Yes. We work with teams directly: AI code and workflow audits, building the guardrails, and coaching your engineers to do AI-assisted work well.',
    a: (
      <>
        Yes. We work with teams directly: AI code and workflow audits, building
        the guardrails, and coaching your engineers to do AI-assisted work well.
        See{' '}
        <Link
          to="/consulting"
          className="underline"
          style={{ textDecorationColor: theme.colors.semantic.info }}
        >
          consulting
        </Link>
        .
      </>
    ),
  },
];

// FAQPage structured data, built from the same source as the rendered list.
const faqJsonLd = {
  '@context': 'https://schema.organization',
  '@type': 'FAQPage',
  mainEntity: faqs.map((item) => ({
    '@type': 'Question',
    name: item.q,
    acceptedAnswer: {
      '@type': 'Answer',
      text: typeof item.a === 'string' ? item.a : (item.answerText ?? ''),
    },
  })),
};

function FaqContent() {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(appHasMounted());

  useEffect(() => {
    markAppMounted();
    setMounted(true);
  }, []);

  const showLoading = !mounted;
  const isDark = mounted && resolvedTheme === 'dark';

  if (showLoading) {
    return (
      <MarketingLayout isLoading={true} isSignedIn={false}>
        <section className="px-6 pt-24 pb-16 md:px-16 md:pt-32">
          <div className="mx-auto max-w-[760px]">
            <Skeleton className="mx-auto mt-2 mb-12 h-12 w-[50%]" />
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="mb-4 h-36 w-full rounded-xl" />
            ))}
          </div>
        </section>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout isLoading={false} isSignedIn={false}>
      <JsonLd data={faqJsonLd} />
      <section className="px-6 pt-24 pb-16 md:px-16 md:pt-32">
        <div className="mx-auto max-w-[760px]">
          <h1
            className="text-gradient-hero mb-12 text-center text-4xl font-bold tracking-tight md:text-5xl"
            style={{ fontFamily: theme.fonts.display, lineHeight: 1.2 }}
          >
            Questions, answered.
          </h1>

          <div className="space-y-4" style={{ fontFamily: theme.fonts.body }}>
            {faqs.map((item, i) => (
              <div
                key={item.q}
                className="rounded-xl p-6"
                style={
                  i % 2 === 0
                    ? {
                        background:
                          theme.box.purple.bg[isDark ? 'dark' : 'light'],
                        border: `1px solid ${theme.box.purple.border[isDark ? 'dark' : 'light']}`,
                      }
                    : {
                        background: 'transparent',
                        border: `1px solid ${theme.box.purple.border[isDark ? 'dark' : 'light']}`,
                      }
                }
              >
                <h2
                  className="mb-2 text-lg font-semibold md:text-xl"
                  style={{ fontFamily: theme.fonts.display }}
                >
                  {item.q}
                </h2>
                <p className="text-foreground/70 text-base">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}

function Faq() {
  return (
    <Suspense
      fallback={
        <MarketingLayout isLoading={true} isSignedIn={false}>
          <section className="px-6 pt-24 pb-16 md:px-16 md:pt-32">
            <div className="mx-auto max-w-[760px]">
              <Skeleton className="mx-auto mb-12 h-12 w-[50%]" />
              <Skeleton className="h-24 w-full rounded-xl" />
            </div>
          </section>
        </MarketingLayout>
      }
    >
      <FaqContent />
    </Suspense>
  );
}
